import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Availability } from '../types';
import { buildSlotParticipantMap } from '../utils/availabilityMap';
import { formatHourLabel, getAppLocale } from '../utils/locale';
import { useHourFormat } from './useHourFormat';

export interface TimeSlot {
  day: string;
  hour: number;
  dayName: string;
  hourLabel: string;
  participants: string[];
  isSelected: boolean;
}

export function useCalendar(
  availability: Availability[],
  participantName: string | null,
  startDate: Date = new Date(),
  dayCount = 7
) {
  const { i18n } = useTranslation();
  const { hourFormat } = useHourFormat();
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const optimisticSlotsRef = useRef<Map<string, boolean>>(new Map());
  const locale = getAppLocale(i18n.language);

  const timeSlots = useMemo(() => {
    const baseDate = new Date(startDate);
    baseDate.setHours(0, 0, 0, 0); // Reset to start of day
    const days = [];
    
    for (let i = 0; i < dayCount; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      
      // Use local date string to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      days.push({
        date: dateString,
        name: date.toLocaleDateString(locale, { weekday: 'short' }),
        fullName: date.toLocaleDateString(locale, { weekday: 'long' }),
      });
    }

    const hours = [];
    for (let hour = 0; hour <= 23; hour++) {
      const label = formatHourLabel(hour, i18n.language, hourFormat);
      hours.push({
        hour,
        label,
      });
    }

    const availabilityMap = buildSlotParticipantMap(availability);

    const slots: TimeSlot[] = [];
    days.forEach(day => {
      hours.forEach(({ hour, label }) => {
        const key = `${day.date}-${hour}`;
        const participants = availabilityMap.get(key) || [];
        const isSelected = selectedSlots.has(key);
        
        slots.push({
          day: day.date,
          hour,
          dayName: day.name,
          hourLabel: label,
          participants,
          isSelected,
        });
      });
    });

    return { slots, days, hours };
  }, [availability, selectedSlots, startDate, dayCount, locale, i18n.language, hourFormat]);

  const toggleSlot = (day: string, hour: number) => {
    const key = `${day}-${hour}`;
    setSelectedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const isSlotSelected = (day: string, hour: number) => {
    return selectedSlots.has(`${day}-${hour}`);
  };

  const setSlotsAvailability = (
    slots: Array<{ day: string; hour: number }>,
    available: boolean
  ) => {
    slots.forEach(({ day, hour }) => {
      optimisticSlotsRef.current.set(`${day}-${hour}`, available);
    });
    setSelectedSlots(prev => {
      const newSet = new Set(prev);
      slots.forEach(({ day, hour }) => {
        const key = `${day}-${hour}`;
        if (available) newSet.add(key);
        else newSet.delete(key);
      });
      return newSet;
    });
  };

  const getSlotParticipants = (day: string, hour: number) => {
    const slot = timeSlots.slots.find(s => s.day === day && s.hour === hour);
    return slot?.participants || [];
  };

  const getOverlapCount = (day: string, hour: number) => {
    return getSlotParticipants(day, hour).length;
  };

  const clearSelection = () => {
    setSelectedSlots(new Set());
  };

  const selectAllSlotsForDay = (day: string) => {
    setSelectedSlots(prev => {
      const newSet = new Set(prev);
      for (let hour = 0; hour <= 23; hour++) {
        newSet.add(`${day}-${hour}`);
      }
      return newSet;
    });
  };

  const userSelectedSlots = useMemo(() => {
    if (!participantName) return [];
    return availability.filter(
      avail => avail.participant === participantName && avail.available
    );
  }, [availability, participantName]);

  useEffect(() => {
    if (!participantName) return;

    const serverSlots = new Set<string>();
    userSelectedSlots.forEach(avail => {
      serverSlots.add(`${avail.day}-${avail.hour}`);
    });

    for (const [key, available] of [...optimisticSlotsRef.current]) {
      if (serverSlots.has(key) === available) {
        optimisticSlotsRef.current.delete(key);
      }
    }

    setSelectedSlots(prev => {
      const next = new Set(serverSlots);
      for (const [key, available] of optimisticSlotsRef.current) {
        if (available) next.add(key);
        else next.delete(key);
      }
      if (prev.size === next.size && [...prev].every(key => next.has(key))) {
        return prev;
      }
      return next;
    });
  }, [userSelectedSlots, participantName]);

  return {
    timeSlots,
    toggleSlot,
    setSlotsAvailability,
    isSlotSelected,
    getSlotParticipants,
    getOverlapCount,
    clearSelection,
    selectAllSlotsForDay,
    selectedSlots: Array.from(selectedSlots),
  };
}
